import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SchoolAnalyticsPage({ token, user }) {
  const { language } = useLanguage();
  const [stats, setStats] = useState(null);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const schoolId = user?.school_id || '';

  useEffect(() => { if (token && schoolId) loadData(); else setLoading(false); }, [token]);

  const loadData = async () => {
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [schoolRes, healthRes, studentsRes, teachersRes, attendanceRes, feesRes] = await Promise.all([
        fetch(`${API_URL}/api/schools/${schoolId}`, { headers }).then(r => r.json()).catch(() => ({})),
        fetch(`${API_URL}/api/school-health/${schoolId}`, { headers }).then(r => r.json()).catch(() => ({})),
        fetch(`${API_URL}/api/students/?school_id=${schoolId}&limit=1`, { headers }).then(r => r.json()).catch(() => ({})),
        fetch(`${API_URL}/api/teachers/?school_id=${schoolId}&limit=1`, { headers }).then(r => r.json()).catch(() => ({})),
        fetch(`${API_URL}/api/attendance/stats?school_id=${schoolId}`, { headers }).then(r => r.json()).catch(() => ({})),
        fetch(`${API_URL}/api/fees/stats?school_id=${schoolId}`, { headers }).then(r => r.json()).catch(() => ({})),
      ]);
      setStats({
        school: schoolRes,
        students: studentsRes.total || studentsRes.students?.length || 0,
        teachers: teachersRes.total || teachersRes.teachers?.length || 0,
        attendance: attendanceRes,
        fees: feesRes,
      });
      if (healthRes.success) setHealth(healthRes);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const gradeColors = { 'A+': 'text-green-600 bg-green-100', 'A': 'text-blue-600 bg-blue-100', 'B': 'text-yellow-600 bg-yellow-100', 'C': 'text-orange-600 bg-orange-100', 'D': 'text-red-600 bg-red-100' };

  if (loading) return <div className="flex justify-center py-16"><div className="animate-spin h-10 w-10 border-b-2 border-orange-500 rounded-full"></div></div>;

  return (
    <div className="space-y-6" data-testid="school-analytics-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-800" style={{fontFamily: 'Outfit, sans-serif'}}>
          {language === 'hi' ? 'स्कूल एनालिटिक्स / School Analytics' : 'School Analytics'}
        </h1>
        <p className="text-gray-500 text-sm mt-1">{stats?.school?.name || ''}</p>
      </div>

      {/* Health Score Card */}
      {health && (
        <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-2xl p-6 text-white" data-testid="health-score-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">{language === 'hi' ? 'School Health Score' : 'School Health Score'}</p>
              <p className="text-4xl font-black mt-1">{health.total_score}<span className="text-lg text-slate-400">/100</span></p>
              <p className="text-sm mt-1 text-slate-300">{health.label}</p>
            </div>
            <div className={`w-20 h-20 rounded-2xl flex items-center justify-center text-3xl font-black ${gradeColors[health.grade] || 'bg-gray-100'}`}>
              {health.grade}
            </div>
          </div>
          {/* Breakdown bars */}
          {health.breakdown && (
            <div className="mt-4 space-y-2">
              {Object.entries(health.breakdown).map(([key, val]) => {
                const labels = { data_completion: language === 'hi' ? 'डेटा पूर्णता' : 'Data', attendance: language === 'hi' ? 'उपस्थिति' : 'Attendance', fee_collection: language === 'hi' ? 'फीस संग्रह' : 'Fees', ai_usage: 'AI', engagement: language === 'hi' ? 'सक्रियता' : 'Activity' };
                const pct = val.max > 0 ? (val.score / val.max) * 100 : 0;
                return (
                  <div key={key} className="flex items-center gap-3">
                    <span className="text-xs text-slate-400 w-20 text-right">{labels[key] || key}</span>
                    <div className="flex-1 bg-white/10 rounded-full h-2">
                      <div className={`h-2 rounded-full ${pct >= 70 ? 'bg-green-400' : pct >= 40 ? 'bg-yellow-400' : 'bg-red-400'}`} style={{width: `${pct}%`}} />
                    </div>
                    <span className="text-xs text-slate-300 w-12">{val.score}/{val.max}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: language === 'hi' ? 'छात्र' : 'Students', value: stats?.students || 0, icon: 'fa-user-graduate', color: 'bg-blue-500' },
          { label: language === 'hi' ? 'शिक्षक' : 'Teachers', value: stats?.teachers || 0, icon: 'fa-chalkboard-teacher', color: 'bg-green-500' },
          { label: language === 'hi' ? 'उपस्थिति %' : 'Attendance %', value: `${stats?.attendance?.average_percentage || health?.breakdown?.attendance?.detail?.split('%')[0] || '—'}`, icon: 'fa-calendar-check', color: 'bg-purple-500' },
          { label: language === 'hi' ? 'Health Grade' : 'Health Grade', value: health?.grade || '—', icon: 'fa-heart-pulse', color: 'bg-red-500' },
        ].map((stat, i) => (
          <div key={i} className="bg-white rounded-xl p-4 border shadow-sm" data-testid={`stat-${i}`}>
            <div className={`w-10 h-10 ${stat.color} rounded-xl flex items-center justify-center mb-3`}>
              <i className={`fa-solid ${stat.icon} text-white`}></i>
            </div>
            <p className="text-2xl font-bold text-gray-800">{stat.value}</p>
            <p className="text-xs text-gray-400">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-xl p-5 border shadow-sm">
        <h3 className="font-semibold text-gray-700 mb-3">{language === 'hi' ? 'Quick Actions' : 'Quick Actions'}</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: language === 'hi' ? 'Data Export' : 'Export Data', route: '/data-export', icon: 'fa-download', color: 'bg-blue-50 text-blue-600' },
            { label: language === 'hi' ? 'Reports' : 'Reports', route: '/reports', icon: 'fa-chart-bar', color: 'bg-green-50 text-green-600' },
            { label: language === 'hi' ? 'Setup Guide' : 'Setup Guide', route: '/setup-guide', icon: 'fa-list-check', color: 'bg-orange-50 text-orange-600' },
            { label: language === 'hi' ? 'AI Features' : 'AI Features', route: '/ai', icon: 'fa-robot', color: 'bg-purple-50 text-purple-600' },
          ].map((action, i) => (
            <a key={i} href={action.route} className={`p-3 rounded-xl ${action.color} text-center hover:shadow-md transition`}>
              <i className={`fa-solid ${action.icon} text-lg block mb-1`}></i>
              <p className="text-xs font-medium">{action.label}</p>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}
